'use client';

import { useIsomorphicEffect } from '@ocodelib/mui/hooks';
import type { IProjectLoader, ProjectFile } from '@repo/project-interface';
import { useEffect, useMemo, useState } from 'react';
import { createPyBasicApp } from './app/create-app';
import type { PyBasicAppType } from './types';

export function usePyBasicAppLoader(options: {
  previewMode: boolean;
  newProjectLoader?: IProjectLoader<ProjectFile[]>;
  initialProjectLoader?: IProjectLoader<ProjectFile[]>;
}) {
  const { previewMode, newProjectLoader, initialProjectLoader } = options;
  const [app, setApp] = useState<PyBasicAppType | undefined>();
  const [enabled, setEnabled] = useState(false);

  useIsomorphicEffect(() => {
    setEnabled(true);
    return () => {
      setEnabled(false);
    };
  }, []);

  useIsomorphicEffect(() => {
    if (!enabled || !app) return;
    return () => {
      app.destroy();
      setApp(undefined);
    };
  }, [enabled, app]);

  useEffect(() => {
    if (!enabled) return;
    if (app) {
      // console.log("app already defined");
      return;
    }
    const ctx = { canceled: false };
    createPyBasicApp({ ctx, previewMode, newProjectLoader, initialProjectLoader }, setApp);
    return () => {
      ctx.canceled = true;
    };
  }, [enabled, app, previewMode, newProjectLoader, initialProjectLoader]);

  return useMemo(
    () => ({
      app,
    }),
    [app],
  );
}
